import React from 'react';
import styled from 'styled-components';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { spacing, fontSize, fontWeight, borderRadius, shadows, colors } from '../styles/theme';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${spacing.md};
`;

const Modal = styled.div`
  background-color: ${colors.surface};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl};
  max-width: 600px;
  width: 100%;
  box-shadow: ${shadows.lg};
  border: 2px solid ${colors.brand};
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: ${spacing.lg};
    max-width: 95%;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
`;

const IconWrapper = styled.div`
  color: ${colors.brand};
  display: flex;
  align-items: center;
`;

const Title = styled.h2`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.bold};
  color: ${colors.brand};
  margin: 0;
`;

const Message = styled.p`
  font-size: ${fontSize.md};
  color: ${colors.text};
  line-height: 1.6;
  margin-bottom: ${spacing.lg};
`;

const InstructionsBox = styled.div`
  background-color: ${colors.background};
  border-radius: ${borderRadius.md};
  padding: ${spacing.md};
  margin-bottom: ${spacing.lg};
  border: 1px solid ${colors.border};
`;

const InstructionsTitle = styled.h3`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  color: ${colors.text};
  margin: 0 0 ${spacing.sm} 0;
`;

const InstructionsList = styled.ul`
  margin: 0;
  padding-left: ${spacing.lg};
  
  li {
    font-size: ${fontSize.sm};
    color: ${colors.text};
    margin-bottom: ${spacing.xs};
    line-height: 1.5;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const UpdateLogBox = styled.div`
  background-color: ${colors.background};
  border-radius: ${borderRadius.md};
  padding: ${spacing.md};
  margin-bottom: ${spacing.lg};
  border: 1px solid ${colors.border};
`;

const UpdateLogTitle = styled.h3`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  color: ${colors.brand};
  margin: 0 0 ${spacing.sm} 0;
`;

const UpdateLogContent = styled.div`
  font-size: ${fontSize.sm};
  color: ${colors.text};
  line-height: 1.6;
  
  ul {
    margin: 0;
    padding-left: ${spacing.lg};
    
    li {
      margin-bottom: ${spacing.xs};
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const RefreshButton = styled.button`
  width: 100%;
  padding: ${spacing.md};
  background-color: ${colors.brand};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${colors.warning};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const WarningNote = styled.div`
  background-color: ${colors.error}20;
  border-left: 3px solid ${colors.error};
  padding: ${spacing.sm};
  margin-bottom: ${spacing.md};
  border-radius: ${borderRadius.sm};
  font-size: ${fontSize.sm};
  color: ${colors.error};
  line-height: 1.5;
`;

export default function UpdateModal({ updateInfo, currentVersion }) {
  const handleRefresh = () => {
    // Hard refresh to clear cache
    window.location.reload(true);
  };

  const renderUpdateLog = () => {
    if (!updateInfo.update_log) return null;

    // If update_log is a string, display it directly
    if (typeof updateInfo.update_log === 'string') {
      return <UpdateLogContent dangerouslySetInnerHTML={{ __html: updateInfo.update_log }} />;
    }

    // If update_log is an object/array (JSONB), format it
    if (typeof updateInfo.update_log === 'object') {
      if (Array.isArray(updateInfo.update_log)) {
        return (
          <UpdateLogContent>
            <ul>
              {updateInfo.update_log.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </UpdateLogContent>
        );
      } else {
        // Handle object format
        return (
          <UpdateLogContent>
            {Object.entries(updateInfo.update_log).map(([key, value]) => (
              <div key={key}>
                <strong>{key}:</strong>
                {Array.isArray(value) ? (
                  <ul>
                    {value.map((item, index) => (
                      <li key={index}>{typeof item === 'object' ? JSON.stringify(item) : item}</li>
                    ))}
                  </ul>
                ) : typeof value === 'object' ? (
                  <pre>{JSON.stringify(value, null, 2)}</pre>
                ) : (
                  <p>{value}</p>
                )}
              </div>
            ))}
          </UpdateLogContent>
        );
      }
    }

    return null;
  };

  return (
    <Overlay>
      <Modal>
        <Header>
          <IconWrapper>
            <AlertTriangle size={32} />
          </IconWrapper>
          <Title>Te oude versie</Title>
        </Header>

        <Message>
          <strong>Refresh een paar keer</strong> totdat deze melding verdwijnt.
        </Message>

        <Message style={{ textAlign: 'center', fontSize: '18px', fontWeight: 600, margin: '16px 0' }}>
          Of
        </Message>

        <Message>
          Doe een <strong>hard refresh</strong>.
        </Message>

        <WarningNote>
          <strong>⚠️ Let op als je Safari gebruikt:</strong> Safari loopt vaak achter met updates en harde refresh werkt niet altijd. 
          Probeer eerst de instructies hieronder, en als dat niet werkt, gebruik bijvoorbeeld Chrome of Edge.
        </WarningNote>

        <InstructionsBox>
          <InstructionsTitle>Hard Refresh instructies per apparaat:</InstructionsTitle>
          <InstructionsList>
            <li>
              <strong>📱 Mac - Safari:</strong> Command + Option + R
            </li>
            <li>
              <strong>🖥️ Mac - Chrome:</strong> Command + R
            </li>
            <li>
              <strong>💻 Windows - Chrome/Edge:</strong> Ctrl + F5 of Ctrl + Shift + Delete
            </li>
            <li>
              <strong>🪟 Windows - Firefox:</strong> Ctrl + Shift + R
            </li>
            <li>
              <strong>📱 iPhone/iPad - Safari:</strong> Instellingen → Safari → Geavanceerd → Websitedata → mijn.webvonk.com → Wis data (daarna moet je opnieuw inloggen)
            </li>
          </InstructionsList>
        </InstructionsBox>

        <UpdateLogBox>
          <UpdateLogTitle>Dit is er veranderd in deze update:</UpdateLogTitle>
          {renderUpdateLog()}
        </UpdateLogBox>

        <RefreshButton onClick={handleRefresh}>
          <RefreshCw size={20} />
          Refresh Pagina Nu
        </RefreshButton>
      </Modal>
    </Overlay>
  );
}
